import { markResolved } from './diceTrace.ts';
/**
 * RWBR — QUALITY CHALLENGE resolution. Rulebook p.vi–viii, p.xiv, p.xvi.
 *
 * "The quality challenge results are the nuts and bolts of the game" (p.iii).
 * Everything in here is load-bearing: a modifier that is one point wrong bends
 * every race the engine will ever run, so each rule below carries the page it
 * came from and each is pinned by a test in challenge.test.ts.
 *
 * SHAPE OF THE TURN
 *   The RACE CAM has named a group and a quality. Every car in that group that
 *   HOLDS the quality challenges, one at a time, TRAILING CAR FIRST (p.vi).
 *   Each challenge is one red/white/blue roll against one defender in the group
 *   above. Winners swap with the car they beat, which is why a car can win a
 *   challenge early in the turn and be knocked back out of its new group by a
 *   later challenger from the group it just left (p.viii).
 *
 * WHAT THIS MODULE DOES NOT DO
 *   PROBLEM FINDER, repair rolls and FILLING A GAP are separate procedures with
 *   their own dice and their own pages. Challenges that generate a PROBLEM
 *   report it as `pendingProblem` and, through the optional `problemResolver`
 *   hook, ask only the one question the challenge rules actually need answered:
 *   did it bring out the yellow? (p.vi — if so, no further challenges this turn.)
 */

import type { Dice } from './dice.ts';
import type { Car, Group, PitCrewGrade, Quality, RaceState } from './types.ts';
import { groupOf, hasQuality, isStarred } from './types.ts';
import { traitChipsOf } from './chips.ts';

export const CITE_DOMINANCE =
  'EZRA-S2 Performance Chip Rules — "Anytime a Challenger and a Defender (Quality Challenge or ' +
  'Duel) have a non-equal number of Performance Chips to spend, the driver with the most current ' +
  'Performance Chip will dominate and control the event."';
import {
  ESCAPE_CHALLENGES,
  canChallengeOutOfLdsg,
  returnToLeadLap,
  type LapsDownTracker,
} from './lapsDown.ts';
import type {
  ChipDelta,
  DiceRecord,
  Modifier,
  PendingProblem,
  PositionDelta,
  RuleEvent,
} from './ruleEvent.ts';
import {
  carAt,
  diffOrder,
  fieldFromState,
  groupAbove,
  membersOf,
  modifierValue,
  nameOf,
  positionOf,
  swapPositions,
} from './ruleEvent.ts';

// ---------------------------------------------------------------------------
// Board geometry — transcribed from the printed boards, not computed
// ---------------------------------------------------------------------------

/** The RACE CAM never names the LEADER (p.ii), so only these three can be called. */
export type CalledGroup = Exclude<Group, 'leader'>;

/** p.vii — "the maximum number of brake symbols that can be accumulated is FIVE." */
export const MAX_BRAKE_SYMBOLS = 5;

/**
 * Board A, TOP group: the WHITE die picks one of six boxes, one car each.
 * Box 1 is 2nd place through box 6 = 7th place.
 */
export const TOP_BOX_POSITION: Readonly<Record<number, number>> = {
  1: 2,
  2: 3,
  3: 4,
  4: 5,
  5: 6,
  6: 7,
};

/**
 * Board B, MIDDLE group: the WHITE die picks one of six boxes, TWO cars each.
 * The RED die's parity picks the half — ODD takes the LEFT card, EVEN the
 * RIGHT (p.vii). On the printed board the LEFT card of each box is the
 * TRAILING of the pair, so odd selects the deeper car.
 */
export const MIDDLE_BOX_POSITION: Readonly<Record<number, { odd: number; even: number }>> = {
  1: { odd: 9, even: 8 },
  2: { odd: 11, even: 10 },
  3: { odd: 13, even: 12 },
  4: { odd: 15, even: 14 },
  5: { odd: 17, even: 16 },
  6: { odd: 19, even: 18 },
};

/** Which group a challenge out of `group` is aimed at. */
export const GROUP_CHALLENGED: Readonly<Record<CalledGroup, Group>> = {
  top: 'leader',
  middle: 'top',
  bottom: 'middle',
};

// ---------------------------------------------------------------------------
// Selecting the defender
// ---------------------------------------------------------------------------

export interface DefenderSlot {
  /** 1-based position the dice pointed at. May be empty if the field is short. */
  position: number;
  /** p.vii — a challenge to the RACE LEADER disregards the white die. */
  whiteIgnored: boolean;
  /** Which half of a MIDDLE box the red die's parity selected, if any. */
  half: 'odd' | 'even' | null;
  /** 1-6 board box the white die selected, if any. */
  boxNumber: number | null;
}

/**
 * p.vii — WHITE picks the box in the group above; in the MIDDLE group the RED
 * die's parity picks which of the two cars in that box.
 */
export function defenderSlot(challengedGroup: Group, red: number, white: number): DefenderSlot {
  switch (challengedGroup) {
    case 'leader':
      // "Challenges to the RACE LEADER, of course, disregard the white die result."
      return { position: 1, whiteIgnored: true, half: null, boxNumber: null };
    case 'top':
      return {
        position: TOP_BOX_POSITION[white],
        whiteIgnored: false,
        half: null,
        boxNumber: white,
      };
    case 'middle': {
      const half = red % 2 === 1 ? 'odd' : 'even';
      return {
        position: MIDDLE_BOX_POSITION[white][half],
        whiteIgnored: false,
        half,
        boxNumber: white,
      };
    }
    case 'bottom':
      throw new Error('nothing challenges INTO the BOTTOM group — it is the back of the field');
  }
}

// ---------------------------------------------------------------------------
// Brake pedal and its bonuses
// ---------------------------------------------------------------------------

export interface BrakeCalc {
  /** Printed "burst" symbols on the defender's card. */
  base: number;
  /** Every bonus that applied, named and valued. */
  bonuses: Modifier[];
  /** base + bonuses, before the ceiling. */
  raw: number;
  /** What the blue die is actually measured against. */
  effective: number;
  /** True when the five-symbol ceiling bit. */
  capped: boolean;
}

/**
 * p.vii — the brake pedal rating can be boosted by, cumulatively:
 *   • the defender HOLDING the called quality            +1  (starred: +2)
 *   • being a challenged driver in the TOP group         +1
 *   • being the challenged race LEADER                   +2
 * and then: "the maximum number of brake symbols that can be accumulated is FIVE."
 *
 * The ceiling is on the TOTAL, not on the bonuses alone. The bonuses alone can
 * never reach five (the richest case is a starred leader at 2 + 2 = 4), so a
 * bonus-only ceiling would be dead text; and capping the total is what keeps a
 * challenge possible at all, since a 5 still loses to a blue 6.
 */
export function brakePedal(
  defender: Car,
  defenderPosition: number,
  calledQuality: Quality | 'TV',
  /** HOUSE RULE D-EZ-86 — clean-air brake symbols for a LEADER/TOP defender. */
  cleanAir = 0,
): BrakeCalc {
  const base = defender.card.brakePedal;
  const bonuses: Modifier[] = [];

  if (hasQuality(defender, calledQuality)) {
    const starred = isStarred(defender, calledQuality);
    bonuses.push({
      name: starred ? 'defender-holds-called-quality-starred' : 'defender-holds-called-quality',
      value: starred ? 2 : 1,
      note: `${nameOf(defender)} holds ${calledQuality}${starred ? '★' : ''} himself (p.vii)`,
    });
  }

  const group = groupOf(defenderPosition);
  if (group === 'top') {
    bonuses.push({
      name: 'defender-in-top-group',
      value: 1,
      note: 'every challenged TOP group driver gets one bonus brake symbol (p.vii)',
    });
  }
  if (group === 'leader') {
    bonuses.push({
      name: 'defender-is-race-leader',
      value: 2,
      note: 'a challenged race LEADER gets two bonus brake symbols (p.vii)',
    });
  }

  if (cleanAir > 0 && (group === 'leader' || group === 'top')) {
    bonuses.push({
      name: 'clean-air-restart',
      value: cleanAir,
      note: 'house rule D-EZ-86 — clean air at the front on a start or restart',
    });
  }

  const raw = base + bonuses.reduce((n, m) => n + modifierValue(m), 0);
  const effective = Math.min(raw, MAX_BRAKE_SYMBOLS);
  const capped = raw > MAX_BRAKE_SYMBOLS;
  if (capped) {
    bonuses.push({
      name: 'brake-symbol-ceiling',
      value: effective - raw,
      note: `no car may accumulate more than ${MAX_BRAKE_SYMBOLS} brake symbols (p.vii)`,
    });
  }

  return { base, bonuses, raw, effective, capped };
}

// ---------------------------------------------------------------------------
// PERFORMANCE CHIPS — p.xiv
// ---------------------------------------------------------------------------

/** Ledger labels. Tests and the broadcast booth both look rolls up by these. */
export const CHALLENGE_ROLL = 'challenge red-white-blue';
export const ODD_EVEN_ROLL = 'odd/even chip die';

/** p.xiv — the extra die rolled only when both sides hold a chip. */
export function oddEvenRoll(face: number | null): DiceRecord[] {
  if (face === null) return [];
  return [{ kind: 'd6', dice: [face], label: ODD_EVEN_ROLL, faces: { oddEven: face } }];
}

export type ChipOutcome =
  | 'none'
  | 'challenger-spent'
  | 'defender-spent'
  | 'both-spend'
  | 'both-retain'
  /**
   * EZRA-S2 — one side held more chips and dominated, but the roll had already
   * gone his way, so nothing was spent. Distinct from 'none', which means chips
   * were never in play: here they decided who would have won if it mattered,
   * and a ledger that called that 'none' would be hiding a live rule.
   */
  | 'dominated-unspent';

export interface ChipContest {
  /** The result after chips. */
  success: boolean;
  outcome: ChipOutcome;
  /** The extra odd/even die, when both sides held a chip. */
  oddEvenDie: number | null;
  chipDeltas: ChipDelta[];
  modifiers: Modifier[];
}

/**
 * p.xiv — chips, resolved the solitaire way:
 *
 *   "To simplify the procedure when playing the game solitaire, it's considered
 *    that a driver who HAS a performance chip automatically SPENDS it to salvage
 *    a failed challenge or defense."
 *
 *   "If BOTH the challenger and defender have a performance chip, then the
 *    red-white-blue roll will determine who wins the challenge, and an
 *    additional odd/even die roll will determine whether both drivers SPEND a
 *    chip [odd], or both drivers RETAIN their chip [even]."
 *
 * Note that exactly one side is always in a position to salvage — a challenge
 * either succeeded (the defence failed) or failed (the challenge did) — so when
 * both hold a chip the pair always cancel and the odd/even roll always happens.
 *
 * Shared with duel.ts: p.xiv grants chips on "a challenge or duel" alike.
 */
export function resolveChipContest(args: {
  rawSuccess: boolean;
  challengerId: string;
  challengerChips: number;
  defenderId: string | null;
  defenderChips: number;
  dice: Dice;
  /** Off for head-to-head play, where chip decisions are made by hand up front. */
  enabled: boolean;
  /**
   * p.iii — a DUEL target is "defenseless … trapped in a track position where
   * he can't respond", so duel.ts passes false. Quality challenges pass true.
   */
  defenderMayDefend: boolean;
  /** Only affects the wording written into the ledger. */
  venue?: 'challenge' | 'duel';
}): ChipContest {
  const venue = args.venue ?? 'challenge';
  const none: ChipContest = {
    success: args.rawSuccess,
    outcome: 'none',
    oddEvenDie: null,
    chipDeltas: [],
    modifiers: [],
  };
  if (!args.enabled) return none;

  const challengerHas = args.challengerChips > 0;
  const defenderHas = args.defenderChips > 0 && args.defenderMayDefend && args.defenderId !== null;

  // EZRA-S2 Performance Chip Rules — THE COUNT DECIDES, NOT MERELY HOLDING ONE.
  //
  //   "Anytime a Challenger and a Defender (Quality Challenge or Duel) have a
  //    NON-EQUAL number of Performance Chips to spend, the driver with the most
  //    current Performance Chip will DOMINATE AND CONTROL the event."
  //
  // ⚠ THIS ENGINE READ THE BASE GAME'S BINARY VERSION — both sides hold one, so
  // they cancel and the raw roll stands. That is right for RB/xiv and wrong for
  // EZRA, and it was invisible until D-EZ-28 made the ceiling of two real: with
  // no cap, "2 v 1" was just one of many counts nobody tracked; with a cap of
  // two it is the ONLY non-equal pairing above zero, and it is the whole point
  // of a second chip. A driver who banks a second chip and then finds it does
  // nothing in a contest is holding a token the rules say should decide it.
  //
  // Found by verifying the maximum against the primary text rather than against
  // our own summary of it.
  if (challengerHas && defenderHas && args.challengerChips !== args.defenderChips) {
    const challengerLeads = args.challengerChips > args.defenderChips;
    const winnerId = (challengerLeads ? args.challengerId : args.defenderId) as string;
    // "Dominate and control" is about the OUTCOME. A driver already getting the
    // result he wants spends nothing — the same restraint the two branches below
    // show, and the reading under which a chip is a lever rather than a tax.
    const wanted = challengerLeads;
    if (args.rawSuccess === wanted) {
      return {
        success: args.rawSuccess,
        outcome: 'dominated-unspent',
        oddEvenDie: null,
        chipDeltas: [],
        modifiers: [
          {
            name: 'chips-dominate',
            value: 0,
            note:
              `${args.challengerChips} v ${args.defenderChips} chips — the roll already went the ` +
              'way the dominant driver wanted, so nothing was spent (EZRA-S2)',
          },
        ],
      };
    }
    return {
      success: wanted,
      outcome: challengerLeads ? 'challenger-spent' : 'defender-spent',
      oddEvenDie: null,
      chipDeltas: [{ driverId: winnerId, delta: -1, reason: CITE_DOMINANCE }],
      modifiers: [
        {
          name: 'chips-dominate',
          value: challengerLeads ? 1 : -1,
          note:
            `${args.challengerChips} v ${args.defenderChips} chips — the driver with the most ` +
            'dominates and controls the event (EZRA-S2)',
        },
      ],
    };
  }

  // EQUAL numbers, both non-zero: "the Chips will not impact the final outcome,
  // however roll a d6, if the result is an ODD number the Performance Chips are
  // consumed by both drivers, if the result is EVEN both drivers retain their
  // Performance Chips." EZRA and RB/xiv agree here.
  if (challengerHas && defenderHas) {
    const die = args.dice.d6();
    const bothSpend = die % 2 === 1;
    return {
      success: args.rawSuccess,
      outcome: bothSpend ? 'both-spend' : 'both-retain',
      oddEvenDie: die,
      chipDeltas: bothSpend
        ? [
            { driverId: args.challengerId, delta: -1, reason: 'both sides held a chip, odd (p.xiv)' },
            { driverId: args.defenderId as string, delta: -1, reason: 'both sides held a chip, odd (p.xiv)' },
          ]
        : [],
      modifiers: [
        {
          name: 'chips-cancel',
          value: 0,
          note:
            `both drivers held a performance chip, so the red-white-blue roll stood; ` +
            `odd/even die ${die} → both ${bothSpend ? 'SPEND' : 'RETAIN'} (p.xiv)`,
        },
      ],
    };
  }

  if (!args.rawSuccess && challengerHas) {
    return {
      success: true,
      outcome: 'challenger-spent',
      oddEvenDie: null,
      chipDeltas: [{ driverId: args.challengerId, delta: -1, reason: `spent to win a failed ${venue} (p.xiv)` }],
      modifiers: [
        { name: `chip-wins-${venue}`, value: 1, note: 'performance chip spent to take the position (p.xiv)' },
      ],
    };
  }

  if (args.rawSuccess && defenderHas) {
    return {
      success: false,
      outcome: 'defender-spent',
      oddEvenDie: null,
      chipDeltas: [{ driverId: args.defenderId as string, delta: -1, reason: `spent to fend off a ${venue} (p.xiv)` }],
      modifiers: [
        { name: `chip-fends-off-${venue}`, value: -1, note: 'performance chip spent to hold position (p.xiv)' },
      ],
    };
  }

  return none;
}

// ---------------------------------------------------------------------------
// One challenge
// ---------------------------------------------------------------------------

export type ChallengeMoment =
  | 'challenge.pass'
  | 'challenge.blocked'
  | 'challenge.chip-win'
  | 'challenge.chip-defend'
  | 'challenge.void-triples'
  | 'challenge.void-triangle'
  | 'challenge.no-defender';

export interface ChallengeResult extends RuleEvent {
  eventType: 'quality-challenge';
  momentTag: ChallengeMoment;
  challengerId: string;
  defenderId: string | null;
  quality: Quality | 'TV';
  /** 1 = the first challenge, 2 = the extra one a starred quality grants (p.vii). */
  attempt: 1 | 2;
  /** The group the challenge was aimed at. */
  challengedGroup: Group;
  slot: DefenderSlot | null;
  /** Did the red die fall inside the challenger's gas pedal? */
  gasOk: boolean;
  /** Did the blue die fall inside the defender's adjusted brake pedal? */
  defended: boolean;
  brake: BrakeCalc | null;
  /** The outcome BEFORE any performance chip changed it. */
  rawSuccess: boolean;
  /** Set when a one-race AUTO WIN grant carried this challenge (D-EZ-15). */
  grantUsed?: 'autoWinChallengeOnce';
  /** The outcome that actually stands. */
  success: boolean;
  chip: ChipOutcome;
  chipDeltas: ChipDelta[];
  pendingProblem: PendingProblem | null;
  /** True when this challenge did not happen at all (triples, triangle, empty slot). */
  voided: boolean;
  /**
   * The challenger was in the laps-down sub-group.
   *
   * A won challenge from there does NOT swap with the MIDDLE defender — EZRA:
   * "instead of exchanging places with the MIDDLE group driver, his card is
   * moved to the last open position of the 'lead lap' sub-group". The swap is
   * therefore skipped here and the sequence applies the real move, because
   * whether he moves at all depends on his PIT CREW grade, which is a race-long
   * question this function has no business answering.
   */
  challengerLapped: boolean;
}

interface Working {
  order: Car[];
  chips: Map<string, number>;
  /**
   * How many of each driver's chips came from a driver TRAIT. Tracked
   * alongside, because a defender beaten into the BOTTOM group forfeits the
   * ordinary ones and keeps these (EZRA-S2 §4.0) — and the forfeit is emitted
   * from here as a delta, so the subtraction has to be right at THIS layer.
   */
  traitChips: Map<string, number>;
  chipDeltas: ChipDelta[];
  spentAutoWins: Set<string>;
}

function chipsOf(w: Working, id: string): number {
  return w.chips.get(id) ?? 0;
}

/** Trait chips still in hand, never more than the driver actually holds. */
function traitChipsInHand(w: Working, id: string): number {
  return Math.min(w.traitChips.get(id) ?? 0, chipsOf(w, id));
}

function applyChipDeltas(w: Working, deltas: ChipDelta[]): void {
  for (const d of deltas) {
    w.chips.set(d.driverId, chipsOf(w, d.driverId) + d.delta);
    // A spend comes out of the ORDINARY pile first (D-EZ-26); the trait count
    // only moves once there is nothing else left for it to have been.
    w.traitChips.set(d.driverId, traitChipsInHand(w, d.driverId));
    w.chipDeltas.push(d);
  }
}

/**
 * Resolve ONE challenge by ONE driver against ONE defender.
 *
 * `challengedGroup` is passed in rather than derived, because a starred second
 * challenge changes target: fail and you re-challenge the SAME group, succeed
 * and you challenge the NEXT one up (p.vii).
 */
function runOneChallenge(
  w: Working,
  challenger: Car,
  quality: Quality | 'TV',
  challengedGroup: Group,
  attempt: 1 | 2,
  dice: Dice,
  useChips: boolean,
  cleanAir = 0,
): ChallengeResult {
  const challengerId = challenger.card.driverId;
  // A starred first challenge can return this car to the lead lap. The second
  // challenge must read that new state, not the original eligibility snapshot.
  const lapped = (w.order.find(c => c.card.driverId === challengerId) ?? challenger).lapsDown === true;
  const from = positionOf(w.order, challengerId);
  const roll = dice.challenge();
  const gas = challenger.card.gasPedal;
  const gasOk = roll.red <= gas;

  const base = {
    eventType: 'quality-challenge' as const,
    challengerId,
    quality,
    attempt,
    challengedGroup,
    dice: [
      {
        kind: 'd6-set' as const,
        dice: [roll.red, roll.white, roll.blue],
        label: CHALLENGE_ROLL,
        faces: { red: roll.red, white: roll.white, blue: roll.blue },
        flags: { triple: roll.triple },
      },
    ],
  };

  // p.vi — "If the dice roll is 'triples' on the red-white-blue dice (i.e., 1-1-1),
  // disregard the challenge and instead roll for a problem for the challenging
  // driver on the PROBLEM FINDER chart."
  if (roll.triple) {
    const modifiers: Modifier[] = [];
    // AMBIGUITY: 6-6-6 satisfies both the triples rule and the triangle symbol
    // (p.xvi, red 6 on a triangle car = an immediate unspecified PROBLEM). The
    // rulebook never says whether that is one problem or two. Resolved as ONE,
    // using the more specific procedure (PROBLEM FINDER), with the triangle
    // recorded as a modifier so the ledger shows it was considered.
    if (challenger.card.triangle && roll.red === 6) {
      modifiers.push({
        name: 'triangle-subsumed-by-triples',
        value: 0,
        note: 'red 6 on a triangle car AND triples — resolved as the single PROBLEM FINDER roll (p.vi, p.xvi)',
      });
    }
    return {
      ...base,
      momentTag: 'challenge.void-triples',
      ruleCitation: 'RB/vi',
      actorIds: [challengerId],
      positionDeltas: [],
      modifiers,
      defenderId: null,
      slot: null,
      gasOk,
      defended: false,
      brake: null,
      rawSuccess: false,
      success: false,
      chip: 'none',
      chipDeltas: [],
      voided: true,
      challengerLapped: lapped,
      pendingProblem: {
        driverId: challengerId,
        source: 'triples',
        specified: true,
        ruleCitation: 'RB/vi',
        description: `${nameOf(challenger)} rolled ${roll.red}-${roll.white}-${roll.blue} — triples void the challenge and send him to the PROBLEM FINDER.`,
      },
      description:
        `Triples! ${nameOf(challenger)} rolls ${roll.red}-${roll.white}-${roll.blue}; the challenge is off ` +
        `and something has gone wrong on his car instead.`,
    };
  }

  // p.xvi — TRIANGLE SYMBOL: "when a red '6' is rolled in any quality challenge
  // or duel, the driver's car immediately experiences an unspecified PROBLEM."
  // AMBIGUITY: "immediately" is read as pre-empting the challenge, so no swap
  // can result. In practice this changes nothing — the hint on p.viii states a
  // challenge can never succeed on a red 6 — unless a card is ever printed with
  // a gas pedal of 6.
  if (challenger.card.triangle && roll.red === 6) {
    return {
      ...base,
      momentTag: 'challenge.void-triangle',
      ruleCitation: 'RB/xvi',
      actorIds: [challengerId],
      positionDeltas: [],
      modifiers: [
        { name: 'triangle-symbol', value: 0, note: 'red 6 on a car carrying the triangle warning symbol (p.xvi)' },
      ],
      defenderId: null,
      slot: null,
      gasOk,
      defended: false,
      brake: null,
      rawSuccess: false,
      success: false,
      chip: 'none',
      chipDeltas: [],
      voided: true,
      challengerLapped: lapped,
      pendingProblem: {
        driverId: challengerId,
        source: 'triangle',
        specified: false,
        ruleCitation: 'RB/xvi',
        description: `${nameOf(challenger)} carries the triangle and rolled a red 6 — an immediate unspecified PROBLEM, straight to a repair roll.`,
      },
      description:
        `${nameOf(challenger)} pushes too hard — red 6 on a car with the triangle warning, and it lets go. ` +
        `No challenge; he's got a problem.`,
    };
  }

  const slot = defenderSlot(challengedGroup, roll.red, roll.white);
  const defender = carAt(w.order, slot.position);

  // The dice can point at a slot no car occupies once attrition has shortened
  // the field, or before FILLING A GAP has been run. Nothing to challenge.
  if (!defender) {
    return {
      ...base,
      momentTag: 'challenge.no-defender',
      ruleCitation: 'RB/vii',
      actorIds: [challengerId],
      positionDeltas: [],
      modifiers: [],
      defenderId: null,
      slot,
      gasOk,
      defended: false,
      brake: null,
      rawSuccess: false,
      success: false,
      chip: 'none',
      chipDeltas: [],
      voided: true,
      challengerLapped: lapped,
      pendingProblem: null,
      description:
        `${nameOf(challenger)} looks up the track at P${slot.position} and finds nobody there — ` +
        `the slot is empty, so there is no challenge to make.`,
    };
  }

  const defenderId = defender.card.driverId;
  const defenderPos = slot.position;
  const brake = brakePedal(defender, defenderPos, quality, cleanAir);

  /*
    THE GREEN THUMBS-UP TRIANGLE — D-EZ-76, the defender half.

    "...but lose their ability to challenge or defend for the rest of the race."

    ⚠ "CANNOT DEFEND" IS NOT "CANNOT BE CHALLENGED", and getting that backwards
    would have made the penalty a REWARD. Treating him as an empty slot voids
    the challenge and he keeps the place; treating him as beaten means the man
    behind only has to have the run. The card is describing a wounded car being
    driven back to the garage area, so: the door is never shut.

    The brake pedal is still computed and still logged. The ledger has to show
    what he WOULD have defended with, or a reader cannot tell this apart from a
    car that simply had a low number printed on it.
  */
  const cannotDefend = defender.cannotRace === true;
  const defended = cannotDefend ? false : roll.blue <= brake.effective;

  // p.vii — "The challenge is successful if TWO things happen: (1) the
  // challenging driver rolls within his 'gas pedal' range, AND (2) the
  // challenged driver rolls OUTSIDE his 'brake pedal' range."
  const rolledSuccess = gasOk && !defended;

  // POST RACE INTERVIEW — "AUTO WIN ONE QUALITY CHALLENGE- Racer automatically
  // wins Quality Challenge. Ignore performance chips." (D-EZ-15, one race.)
  //
  // BOTH HALVES OF THAT SENTENCE ARE IMPLEMENTED. The challenge succeeds
  // regardless of the dice, AND the chip contest is skipped entirely — "ignore
  // performance chips" means the defender cannot buy the place back, which is
  // the only thing that clause can be about. The dice are still rolled and
  // still logged: the sheet says he wins, not that no challenge happened.
  const autoWin = challenger.grants?.autoWinChallengeOnce === true && !w.spentAutoWins.has(challengerId);
  if (autoWin) w.spentAutoWins.add(challengerId);
  const rawSuccess = autoWin || rolledSuccess;

  const contest: ChipContest = autoWin
    ? {
        success: true,
        outcome: 'none' as ChipOutcome,
        chipDeltas: [] as ChipDelta[],
        modifiers: [
          {
            name: 'interview grant — AUTO WIN ONE QUALITY CHALLENGE',
            value: 'spent',
            detail:
              'RWBR_postraceinterview.pdf — "Racer automatically wins Quality Challenge. ' +
              'Ignore performance chips."',
          },
        ],
        oddEvenDie: null,
      }
    : resolveChipContest({
        rawSuccess,
        challengerId,
        challengerChips: chipsOf(w, challengerId),
        defenderId,
        defenderChips: chipsOf(w, defenderId),
        dice,
        enabled: useChips,
        // A man who cannot defend cannot buy the place back either — spending a
        // chip to fend off a challenge IS defending.
        defenderMayDefend: !cannotDefend,
      });

  const modifiers = [...brake.bonuses, ...contest.modifiers];
  if (cannotDefend) {
    modifiers.push({
      name: 'cannot-defend',
      value: 0,
      note:
        `${nameOf(defender)} is running wounded after a failed repair and cannot defend — ` +
        `his ${brake.effective} brake does not apply (2025 set, green triangle)`,
    });
  }
  const positionDeltas: PositionDelta[] = [];

  if (contest.success && !lapped) {
    swapPositions(w.order, from, defenderPos);
    positionDeltas.push({ driverId: challengerId, from, to: defenderPos });
    positionDeltas.push({ driverId: defenderId, from: defenderPos, to: from });
  }
  // A won challenge from the LDSG is a challenge to get the lap back, not a
  // pass. The defender keeps his place; the move is applied by the sequence,
  // subject to the PIT CREW ladder.
  applyChipDeltas(w, contest.chipDeltas);

  // p.xv — "performance chips cannot be carried by drivers in the BOTTOM group."
  // A defender beaten out of the MIDDLE group forfeits anything he still holds
  // — EXCEPT chips a driver trait awarded him, which EZRA-S2 §4.0 suspends
  // instead of taking away. Those are left in his pile here and parked by
  // `renumber` when the field is stamped, which is the moment his position
  // becomes a fact rather than a prediction.
  const forfeits: ChipDelta[] = [];
  const ordinaryHeld = chipsOf(w, defenderId) - traitChipsInHand(w, defenderId);
  if (contest.success && !lapped && groupOf(from) === 'bottom' && ordinaryHeld > 0) {
    forfeits.push({
      driverId: defenderId,
      delta: -ordinaryHeld,
      reason: 'chips cannot be carried in the BOTTOM group (p.xv)',
    });
    applyChipDeltas(w, forfeits);
    modifiers.push({
      name: 'chip-forfeit-bottom-group',
      value: forfeits[0].delta,
      note: `${nameOf(defender)} was pushed into the BOTTOM group and forfeits his chip(s) (p.xv)`,
    });
  }

  const momentTag: ChallengeMoment =
    contest.outcome === 'challenger-spent'
      ? 'challenge.chip-win'
      : contest.outcome === 'defender-spent'
        ? 'challenge.chip-defend'
        : contest.success
          ? 'challenge.pass'
          : 'challenge.blocked';

  const gasLine = `red ${roll.red} v ${gas} gas${gasOk ? '' : ' — no run'}`;
  const brakeLine =
    `blue ${roll.blue} v ${brake.effective} brake ` +
    `(${brake.base} printed${brake.bonuses.length ? ' ' + brake.bonuses.map((b) => (modifierValue(b) >= 0 ? `+${modifierValue(b)}` : `${modifierValue(b)}`)).join(' ') : ''}` +
    `${brake.capped ? `, ceiling ${MAX_BRAKE_SYMBOLS}` : ''})`;

  let description: string;
  if (contest.success && lapped) {
    description = `${nameOf(challenger)} wins a lap-back challenge against ${nameOf(defender)}. ` +
      `${nameOf(defender)} keeps P${defenderPos}; the pit-crew requirement decides the return to the lead lap.`;
  } else if (contest.success && contest.outcome === 'challenger-spent') {
    description =
      `${nameOf(challenger)} had nothing for ${nameOf(defender)} on the dice — ${gasLine}, ${brakeLine} — ` +
      `but cashes a performance chip and takes P${defenderPos} anyway.`;
  } else if (!contest.success && contest.outcome === 'defender-spent') {
    description =
      `${nameOf(challenger)} was by him — ${gasLine}, ${brakeLine} — but ${nameOf(defender)} burns a ` +
      `performance chip and keeps P${defenderPos}.`;
  } else if (contest.success && cannotDefend) {
    // Without this the ledger reads "blue 2 v 5 brake ... they swap", which is
    // an arithmetic error to anybody checking the work. Say why instead.
    description =
      `${nameOf(challenger)} comes up on ${nameOf(defender)} — ${gasLine} — and there is nothing ` +
      `coming back. ${nameOf(defender)} is wounded and cannot defend. ` +
      `They swap: P${defenderPos} to ${nameOf(challenger)}, P${from} to ${nameOf(defender)}.`;
  } else if (contest.success) {
    description =
      `${nameOf(challenger)} gets a run on ${nameOf(defender)} — ${gasLine}, ${brakeLine}. ` +
      `They swap: P${defenderPos} to ${nameOf(challenger)}, P${from} to ${nameOf(defender)}.`;
  } else {
    description =
      `${nameOf(challenger)} takes a look at ${nameOf(defender)} — ${gasLine}, ${brakeLine} — ` +
      `and ${gasOk ? `${nameOf(defender)} shuts the door` : 'never had the speed to make the move'}. No change.`;
  }
  if (contest.oddEvenDie !== null) {
    description +=
      ` Both cars had a chip; the odd/even die shows ${contest.oddEvenDie}, so both ` +
      `${contest.outcome === 'both-spend' ? 'spend' : 'keep'} theirs.`;
  }

  return {
    ...base,
    momentTag,
    ruleCitation: 'RB/vii',
    actorIds: [challengerId, defenderId],
    positionDeltas,
    modifiers,
    dice: [...base.dice, ...oddEvenRoll(contest.oddEvenDie)],
    defenderId,
    slot,
    gasOk,
    defended,
    brake,
    rawSuccess,
    ...(autoWin ? { grantUsed: 'autoWinChallengeOnce' as const } : {}),
    success: contest.success,
    chip: contest.outcome,
    chipDeltas: [...contest.chipDeltas, ...forfeits],
    voided: false,
    challengerLapped: lapped,
    pendingProblem: null,
    description,
  };
}

// ---------------------------------------------------------------------------
// The whole group's challenges
// ---------------------------------------------------------------------------

/**
 * What the caller's PROBLEM machinery reports back. The challenge rules need
 * exactly one fact from it (p.vi): did the PROBLEM bring out a yellow flag, in
 * which case no further challenges happen this turn.
 */
export interface ProblemOutcome {
  yellow: boolean;
  description?: string;
}

export type ProblemResolver = (problem: PendingProblem, dice: Dice) => ProblemOutcome;

export interface ChallengeSequenceOptions {
  /**
   * Resolves a PROBLEM raised by triples or a triangle. If omitted, the problem
   * is still reported in `pendingProblems` and the sequence assumes no yellow —
   * the caller must resolve it before the turn ends either way.
   */
  problemResolver?: ProblemResolver;
  /** p.xiv solitaire auto-spend. Turn off for head-to-head, where chips are declared by hand. */
  useChips?: boolean;
  /**
   * The race's laps-down memory. Absent = the optional rule is off and a BOTTOM
   * challenge is an ordinary swap, exactly as the base rulebook has it.
   */
  lapsDown?: LapsDownTracker | null;
  /**
   * Drivers who may not challenge this turn, whatever they hold.
   *
   * Exists for one printed rule: EZRA-S2 §4.0's Re-Start Expert — *"If the
   * driver fails the duel that completes their turn."* A driver whose turn is
   * over cannot then take part in the turn's quality challenge, and there was
   * no way to say that before.
   *
   * He is excluded from `challengerOrder` rather than rolled and discarded,
   * because a roll whose only outcome is nothing consumes dice and repaints
   * every seeded race downstream — the same reasoning the grade-E LDSG filter
   * above is written to.
   */
  excludeChallengers?: ReadonlySet<string>;
  /**
   * HOUSE RULE D-EZ-86 — CLEAN AIR ON RESTARTS. Extra brake symbols for a
   * defender in the LEADER or TOP group, on the green-flag start and the first
   * turn after every caution. 0 / absent = the printed rule, unchanged. Still
   * subject to the five-symbol ceiling (p.vii), so a blue 6 always gets by.
   */
  cleanAir?: number;
}

export interface QualityChallengeCall {
  quality: Quality | 'TV';
  group: CalledGroup;
}

export interface ChallengeSequenceResult extends RuleEvent {
  eventType: 'quality-challenge-sequence';
  momentTag: 'challenge.sequence';
  call: QualityChallengeCall;
  /** Everyone who was entitled to challenge, in the order they got to (trailing first). */
  challengerOrder: string[];
  challenges: ChallengeResult[];
  /** Running order after every challenge resolved, index 0 = leader. */
  finalOrder: string[];
  chipDeltas: ChipDelta[];
  pendingProblems: PendingProblem[];
  /** True when a yellow from a triples PROBLEM ended the challenges early (p.vi). */
  halted: boolean;
  haltReason: string | null;
  /**
   * Every won challenge made from the laps-down sub-group, escaped or banked.
   *
   * Reported rather than folded into `positionDeltas` because a grade-D car's
   * first win moves nobody — it is a real result with no visible consequence,
   * and those are the ones that rot unnoticed.
   */
  ldsgEscapes: LdsgEscape[];
  /**
   * D-EZ-104 — the LEADER and TOP group (positions 1-7) and their spendable
   * chips as they stood when the sequence BEGAN, before any of its dice. A
   * presentation receipt only: nothing reads it to decide anything, and it is
   * written down here because only here is it exactly true — restart events,
   * drafting and multiple-event problems can all move the front of the field
   * between the board the player last saw and the first challenge. The stakes
   * card uses it to name the leader a challenge is aimed at without reading
   * the challenge's own outcome. Absent on races saved before it existed.
   */
  opening?: { id: string; chips: number }[];
}

/**
 * Resolve every challenge the RACE CAM's quality call produces, in order.
 *
 * p.vi — "ONLY drivers in the specific group that HAVE the quality indicated by
 * the metallic dice roll will get to challenge … in ascending order. That is,
 * the trailing drivers challenge first, and the leading drivers challenge last."
 */
// ---------------------------------------------------------------------------
// The laps-down escape
// ---------------------------------------------------------------------------

/** One car's attempt to challenge its way back onto the lead lap. */
export interface LdsgEscape {
  driverId: string;
  grade: PitCrewGrade;
  /** Wins banked toward the escape AFTER this challenge. */
  banked: number;
  /** How many this grade needs. */
  needed: number;
  /** Did he actually get his lap back on this one? */
  escaped: boolean;
  note: string;
}

/**
 * Apply a laps-down challenger's result.
 *
 * EZRA: "If the LDSG Challenger wins the challenge, the driver moves to the last
 * position in the LLSG, subject to Pit crew rating (A-B-C automatic, D has to
 * successfully Challenge twice, E can never challenge out of the LDSG)."
 *
 * A grade-D car's first win is BANKED, not spent: he stays a lap down and needs
 * a second one. That is the only place in this engine where a won challenge
 * moves nobody, so it is reported explicitly rather than being silently
 * absorbed — a rule that does nothing visible is a rule nobody can tell is
 * broken.
 */
function applyLdsgResult(
  w: Working,
  tracker: LapsDownTracker | null,
  challenger: Car,
  result: ChallengeResult,
  out: LdsgEscape[],
): void {
  if (!result.challengerLapped || !result.success) return;
  const id = challenger.card.driverId;
  const grade = challenger.card.pitCrewGrade;
  const needed = ESCAPE_CHALLENGES[grade] ?? 0;

  // With no tracker the rule is off for bookkeeping purposes, so a win escapes
  // immediately for any grade that can escape at all. This is the head-to-head
  // path, where a person is counting D's two wins on their own sheet.
  const escaped = tracker ? tracker.bankWin(id, grade) : needed <= 1;
  const banked = tracker ? (escaped ? needed : tracker.winsBanked(id)) : needed;

  if (escaped) {
    returnToLeadLap(w.order, id, 'challenged his way back onto the lead lap');
    tracker?.cameBack(id);
  }
  out.push({
    driverId: id,
    grade,
    banked,
    needed,
    escaped,
    note: escaped
      ? `${nameOf(challenger)} gets his lap back — into the last spot on the lead lap.`
      : `${nameOf(challenger)} wins it, but a grade-${grade} crew needs ${needed} and he has ${banked}. Still a lap down.`,
  });
}

/**
 * WHO WENT AT WHOM — D-EZ-53.
 *
 * Howard, 18 Aug 2026, reading the ledger on turn 30:
 *
 *   "I think in this situation it was Boone Wimmer that challenged the leader
 *    (I might be wrong) but if it was Boone Wimmer we ought to denote who
 *    challenged who in the ledger."
 *
 * He was right, and "I might be wrong" is the whole problem: the ledger card
 * said *"HEAT in the TOP group: 1 car had it, 1 challenge made, 0 stuck"* and
 * left the reader to work out the names off the board. The modifiers underneath
 * named the DEFENDER — "Zeke Ashford holds HEAT himself" — so the one man the
 * card identified was the one who did not do anything.
 *
 * ⚠ THE NAMES WERE NEVER MISSING FROM THE DATA. Every `ChallengeResult` carries
 * `actorIds[0]` (the challenger) and `defenderId`, and each one's own
 * `description` names both. Only the SUMMARY was anonymous — and the summary is
 * the line the ledger prints. This is a reporting fix, not a rules change: no
 * roll, no order and no result moves, and `challenge.test.ts` asserts that.
 *
 * Kept to a clause per challenge, and capped: a BOTTOM-group call can produce
 * twenty of these, and a ledger card that runs to a paragraph is one nobody
 * reads. Past four, the rest are counted rather than named — the individual
 * events are all still there underneath, each naming both men.
 */
export function whoChallengedWhom(
  field: readonly Car[],
  challenges: readonly ChallengeResult[],
): string {
  const named = new Map(field.map((c) => [c.card.driverId, nameOf(c)]));
  // `defenderId` is nullable — a challenge with nobody in front of it (p.vi's
  // no-defender case) is a real result and it still has a challenger worth
  // naming, which is exactly the line that used to be blank.
  const name = (id: string | null | undefined) => (id ? (named.get(id) ?? id) : 'nobody ahead');

  const SHOWN = 4;
  const clauses = challenges.slice(0, SHOWN).map((c) => {
    const challenger = name(c.actorIds[0]);
    const defender = name(c.defenderId);
    if (c.voided) return `${challenger} at ${defender} — voided`;
    if (c.success && c.challengerLapped) return `${challenger} wins a lap-back check against ${defender}`;
    return c.success ? `${challenger} past ${defender}` : `${challenger} at ${defender}, no change`;
  });

  const rest = challenges.length - clauses.length;
  return `${clauses.join('; ')}${rest > 0 ? `; and ${rest} more` : ''}.`;
}

export function resolveQualityChallenges(
  state: RaceState,
  call: QualityChallengeCall,
  dice: Dice,
  options: ChallengeSequenceOptions = {},
): ChallengeSequenceResult {
  const useChips = options.useChips ?? true;
  const startOrder = fieldFromState(state);
  const w: Working = {
    order: [...startOrder],
    chips: new Map(startOrder.map((c) => [c.card.driverId, c.chips])),
    traitChips: new Map(startOrder.map((c) => [c.card.driverId, traitChipsOf(c)])),
    chipDeltas: [],
    spentAutoWins: new Set(),
  };

  // AMBIGUITY: the entitled-challenger list is snapshotted before any challenge
  // is resolved. The rulebook fixes the group and quality when the RACE CAM is
  // read and never says a car knocked BACK into the called group mid-turn earns
  // a challenge of its own; p.viii's note that a winner can be "moved back close
  // to where he started" describes that as a demotion, not a new opportunity.
  // EZRA: "Any BOTTOM group challenge result always includes both sub-groups,
  // STARTING WITH the 'laps down' sub-group, and moving to the 'lead lap'
  // sub-group." That is already the order this produces and it is not a
  // coincidence: the base rule is trailing-drivers-first (p.vi) and the LDSG is
  // by construction the tail of the field, so the printed EZRA ordering falls
  // out of the printed base ordering with nothing added. Asserted by test so it
  // stays true if either rule is ever touched.
  const challengers = membersOf(w.order, call.group)
    .filter((c) => hasQuality(c, call.quality))
    // EZRA-S2 §4.0 RS — a driver whose turn was completed by a failed restart
    // duel takes no further part in it.
    .filter((c) => !options.excludeChallengers?.has(c.card.driverId))
    /*
      THE GREEN THUMBS-UP TRIANGLE — D-EZ-76, the challenger half.

      2025 set: a driver carrying the green triangle who fails the REPAIR roll
      on a PROBLEM result *"instead go[es] to the top (last place) of the BOTTOM
      group stack, but lose[s] their ability to challenge or defend for the rest
      of the race."* `turn.ts` sets `cannotRace` the moment that happens.

      Filtered out rather than rolled and denied, for the same reason the LDSG
      filter two lines down gives: a roll whose only outcome is nothing is not a
      roll the sheet asks for, and rolling it anyway consumes dice and repaints
      every seeded race after it.
    */
    .filter((c) => !c.cannotRace)
    // "E can never challenge out of the LDSG" — note the wording: he does not
    // CHALLENGE, rather than challenging and being denied. A roll whose only
    // possible outcome is nothing is not a roll the sheet asks for, and rolling
    // it anyway would consume dice and repaint every seeded race downstream.
    .filter((c) => !(c.lapsDown && !canChallengeOutOfLdsg(c.card.pitCrewGrade)))
    .sort((a, b) => positionOf(w.order, b.card.driverId) - positionOf(w.order, a.card.driverId));

  const challenges: ChallengeResult[] = [];
  const pendingProblems: PendingProblem[] = [];
  const ldsgEscapes: LdsgEscape[] = [];
  let halted = false;
  let haltReason: string | null = null;

  for (const challenger of challengers) {
    if (halted) break;

    const first = runOneChallenge(
      w,
      challenger,
      call.quality,
      GROUP_CHALLENGED[call.group],
      1,
      dice,
      useChips,
      options.cleanAir ?? 0,
    );
    markResolved([first], dice.rolls);
    challenges.push(first);
    applyLdsgResult(w, options.lapsDown ?? null, challenger, first, ldsgEscapes);

    if (first.pendingProblem) {
      pendingProblems.push(first.pendingProblem);
      const outcome = options.problemResolver?.(first.pendingProblem, dice) ?? { yellow: false };
      // p.vi — "if a YELLOW FLAG results from a PROBLEM result on 'triples,'
      // no further challenges are conducted that turn."
      if (outcome.yellow) {
        halted = true;
        haltReason = `YELLOW flag from ${nameOf(challenger)}'s PROBLEM — no further challenges this turn (p.vi)`;
      }
      // A car that has just had a PROBLEM is being set aside for a repair roll;
      // it does not go on to take a starred second challenge.
      continue;
    }

    // p.vii — "If a challenging driver has a star symbol after the indicated
    // quality, he gets TWO chances to challenge on the same turn. If the first
    // challenge fails, he can challenge again into the SAME group. If successful
    // on the first challenge, he can challenge into the NEXT group."
    if (!isStarred(challenger, call.quality)) continue;

    let secondTarget: Group | null;
    if (first.success) {
      const nowIn = groupOf(positionOf(w.order, challenger.card.driverId));
      secondTarget = groupAbove(nowIn);
    } else {
      secondTarget = first.challengedGroup;
    }

    // AMBIGUITY: a starred TOP-group challenger who beats the LEADER has no
    // "NEXT group" to challenge into. Reading: the second challenge is simply
    // spent — he is already leading the race, which is what the rule was for.
    if (secondTarget === null) continue;

    const second = runOneChallenge(
      w,
      challenger,
      call.quality,
      secondTarget,
      2,
      dice,
      useChips,
      options.cleanAir ?? 0,
    );
    markResolved([second], dice.rolls);
    challenges.push(second);
    applyLdsgResult(w, options.lapsDown ?? null, challenger, second, ldsgEscapes);
    if (second.pendingProblem) {
      pendingProblems.push(second.pendingProblem);
      const outcome = options.problemResolver?.(second.pendingProblem, dice) ?? { yellow: false };
      if (outcome.yellow) {
        halted = true;
        haltReason = `YELLOW flag from ${nameOf(challenger)}'s PROBLEM — no further challenges this turn (p.vi)`;
      }
    }
  }

  const positionDeltas = diffOrder(startOrder, w.order);
  const passes = challenges.filter((c) => c.success).length;
  const actorIds = [
    ...new Set(challenges.flatMap((c) => c.actorIds)),
  ];

  return {
    eventType: 'quality-challenge-sequence',
    momentTag: 'challenge.sequence',
    ruleCitation: 'RB/vi-vii',
    actorIds,
    positionDeltas,
    modifiers: challenges.flatMap((c) => c.modifiers),
    dice: challenges.flatMap((c) => c.dice),
    description:
      `${call.quality} in the ${call.group.toUpperCase()} group: ` +
      `${challengers.length} car${challengers.length === 1 ? '' : 's'} had it, ` +
      `${challenges.length} challenge${challenges.length === 1 ? '' : 's'} made, ` +
      `${passes} stuck.` +
      (challenges.length > 0 ? ` ${whoChallengedWhom(w.order, challenges)}` : '') +
      (halted ? ` ${haltReason}` : ''),
    call,
    challengerOrder: challengers.map((c) => c.card.driverId),
    challenges,
    finalOrder: w.order.map((c) => c.card.driverId),
    chipDeltas: w.chipDeltas,
    pendingProblems,
    halted,
    haltReason,
    ldsgEscapes,
    opening: startOrder.slice(0, 7).map((c) => ({ id: c.card.driverId, chips: c.chips })),
  };
}
